import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import GroupsIcon from '@mui/icons-material/Groups';
import LockIcon from '@mui/icons-material/Lock';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import PolicyIcon from '@mui/icons-material/Policy';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import ShieldIcon from '@mui/icons-material/Shield';
import SyncIcon from '@mui/icons-material/Sync';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { useTranslation } from 'react-i18next';
import { useViewAudit } from '../../utils/viewAudit';
import IconPicker from '../../components/IconPicker';
import { getSidebarIcon } from '../../icons/sidebarIcons';
import {
  SMART_COLORS as COLORS,
  smartCardSx as cardSx,
  smartTableContainerSx,
  smartTableHeadSx,
  smartTableSx,
} from '../../theme/smartStableTheme';

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');

// ---------------------------------------------------------------------------
// Translations
// ---------------------------------------------------------------------------
// All user-facing strings live in `public/locales/{ar,en,pt}/translation.json`
// under the `access_control_center` namespace. The `useAccessControlText()`
// hook below returns a Proxy that resolves `text.someKey` to
// `t('access_control_center.someKey')` so the rest of the component can keep
// its dot-access style without spelling out the namespace at every callsite.
//
// The COPY object that follows is kept ONLY as a fallback for the (rare) case
// where the JSON files fail to load — it mirrors the same keys exactly.

const COPY = {
  ar: {
    title: 'مركز الصلاحيات',
    subtitle:
      'إدارة مجموعات الصلاحيات وروابط القائمة الجانبية وصلاحيات الوصول للصفحات من مكان واحد.',
    tabGroups: 'مجموعات الصلاحيات',
    tabMenus: 'القائمة الجانبية',
    tabPages: 'صلاحيات الوصول للصفحات',
    refresh: 'تحديث',
    syncPages: 'مزامنة الصفحات',
    save: 'حفظ التغييرات',
    cancel: 'إلغاء',
    delete: 'حذف',
    edit: 'تعديل',
    search: 'بحث...',
    saved: 'تم الحفظ بنجاح',
    adminWarning:
      'مركز الصلاحيات متاح فقط للحساب الإداري العام. سجّل الدخول بحساب System Admin للتعديل.',
    // Groups tab
    addGroup: 'مجموعة جديدة',
    editGroup: 'تعديل المجموعة',
    groupName: 'اسم المجموعة بالإنجليزية',
    groupNameAr: 'اسم المجموعة بالعربية',
    groupDescription: 'الوصف',
    systemAdmin: 'مدير النظام',
    systemAdminHint:
      'مدير النظام لديه صلاحية كاملة دائماً ولا يمكن تعديله أو حذفه — هذا هو خط الأمان للمالك.',
    pagesGranted: 'صفحات مسموح بها',
    menusGranted: 'روابط مرئية',
    usersInGroup: 'مستخدمون',
    statusActive: 'نشطة',
    statusInactive: 'معطلة',
    groupActive: 'مجموعة نشطة',
    confirmDeleteGroup: 'هل أنت متأكد من حذف هذه المجموعة؟',
    cannotDeleteHasUsers: 'لا يمكن حذف مجموعة فيها مستخدمون. أعد تعيينهم أولاً.',
    cannotDeleteSystemAdmin: 'لا يمكن حذف مجموعة مدير النظام.',
    deleteGroupTitle: 'حذف المجموعة',
    deleteEmptyGroupMessage: 'لا يوجد مستخدمون في هذه المجموعة. هل تريد حذفها نهائياً؟',
    deleteGroupHasUsers: 'هذه المجموعة فيها {n} مستخدم/مستخدمين. اختر مجموعة بديلة لنقلهم إليها قبل الحذف.',
    pickTargetGroup: 'اختر مجموعة بديلة',
    targetGroupLabel: 'المجموعة البديلة',
    reassignAndDelete: 'نقل المستخدمين ثم حذف',
    reassignedAndDeleted: 'تم نقل {n} مستخدم وحذف المجموعة بنجاح.',
    usersInThisGroup: 'المستخدمون في هذه المجموعة',
    // Menus tab
    addMenu: 'رابط جديد',
    editMenu: 'تعديل الرابط',
    textKey: 'النص الظاهر',
    path: 'المسار',
    pageName: 'مفتاح الصفحة',
    permissionKey: 'مفتاح الصلاحية',
    icon: 'الأيقونة',
    parent: 'الرابط الأب',
    none: 'لا يوجد',
    sort: 'الترتيب',
    active: 'نشط',
    menuVisibility: 'إظهار للمجموعات',
    menuVisibilityHint:
      'حدّد المجموعات التي ترى هذا الرابط في القائمة الجانبية. إخفاء الرابط لا يلغي صلاحية الوصول للصفحة (يُدار في التبويب الثالث).',
    pickGroupToEdit: 'اختر مجموعة لعرض/تعديل صلاحيات الإطلاع',
    visible: 'ظاهر',
    hidden: 'مخفي',
    // Pages tab
    pickGroup: 'اختر المجموعة',
    pickGroupHint:
      'صلاحية الوصول مستقلة عن الإظهار في القائمة. يمكن منح وصول لصفحة فرعية (مثل صفحات System Settings) دون إضافتها للقائمة الجانبية.',
    allPages: 'كل الصفحات',
    enabledPages: 'مسموح بها',
    selectAll: 'تحديد الكل',
    clearAll: 'إلغاء الكل',
    pageNotLinked: 'صفحة فرعية',
    pageInMenu: 'في القائمة',
    noResults: 'لا توجد نتائج للبحث الحالي',
  },
  en: {
    title: 'Access Control Center',
    subtitle:
      'Manage permission groups, sidebar links, and page access from a single place.',
    tabGroups: 'Permission groups',
    tabMenus: 'Sidebar menu',
    tabPages: 'Page access',
    refresh: 'Refresh',
    syncPages: 'Sync pages',
    save: 'Save changes',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    search: 'Search...',
    saved: 'Saved successfully',
    adminWarning:
      'The Access Control Center is only available for the System Admin account. Sign in as System Admin to make changes.',
    addGroup: 'New group',
    editGroup: 'Edit group',
    groupName: 'English name',
    groupNameAr: 'Arabic name',
    groupDescription: 'Description',
    systemAdmin: 'System Admin',
    systemAdminHint:
      'System Admin always has full access and cannot be edited or deleted — this is the owner safety net.',
    pagesGranted: 'Pages allowed',
    menusGranted: 'Menus visible',
    usersInGroup: 'Users',
    statusActive: 'Active',
    statusInactive: 'Disabled',
    groupActive: 'Group active',
    confirmDeleteGroup: 'Delete this group?',
    cannotDeleteHasUsers: 'Cannot delete a group with users. Re-assign them first.',
    cannotDeleteSystemAdmin: 'The System Admin group cannot be deleted.',
    deleteGroupTitle: 'Delete group',
    deleteEmptyGroupMessage: 'No users belong to this group. Delete it permanently?',
    deleteGroupHasUsers: 'This group has {n} user(s). Pick a target group to move them to before deleting.',
    pickTargetGroup: 'Pick a target group',
    targetGroupLabel: 'Target group',
    reassignAndDelete: 'Reassign and delete',
    reassignedAndDeleted: 'Reassigned {n} users and deleted the group.',
    usersInThisGroup: 'Users in this group',
    addMenu: 'New link',
    editMenu: 'Edit link',
    textKey: 'Display text',
    path: 'Path',
    pageName: 'Page key',
    permissionKey: 'Permission key',
    icon: 'Icon',
    parent: 'Parent link',
    none: 'None',
    sort: 'Order',
    active: 'Active',
    menuVisibility: 'Visible to groups',
    menuVisibilityHint:
      'Pick the groups that see this link in the sidebar. Hiding a link does NOT remove page access (managed in the third tab).',
    pickGroupToEdit: 'Pick a group to see/edit menu visibility',
    visible: 'Visible',
    hidden: 'Hidden',
    pickGroup: 'Pick group',
    pickGroupHint:
      'Page access is independent of sidebar visibility. Sub-pages (such as System Settings sub-pages) can be granted without adding them to the sidebar.',
    allPages: 'All pages',
    enabledPages: 'Allowed',
    selectAll: 'Select all',
    clearAll: 'Clear all',
    pageNotLinked: 'Sub-page',
    pageInMenu: 'In sidebar',
    noResults: 'No results for the current search',
  },
  pt: {
    title: 'Centro de Controle de Acesso',
    subtitle:
      'Gerencie grupos de permissão, links da barra lateral e acesso a páginas em um só lugar.',
    tabGroups: 'Grupos de permissão',
    tabMenus: 'Barra lateral',
    tabPages: 'Acesso a páginas',
    refresh: 'Atualizar',
    syncPages: 'Sincronizar páginas',
    save: 'Salvar alterações',
    cancel: 'Cancelar',
    delete: 'Excluir',
    edit: 'Editar',
    search: 'Pesquisar...',
    saved: 'Salvo com sucesso',
    adminWarning: 'Apenas o System Admin pode usar este painel.',
    addGroup: 'Novo grupo',
    editGroup: 'Editar grupo',
    groupName: 'Nome em inglês',
    groupNameAr: 'Nome em árabe',
    groupDescription: 'Descrição',
    systemAdmin: 'System Admin',
    systemAdminHint:
      'System Admin sempre tem acesso total e não pode ser editado ou excluído.',
    pagesGranted: 'Páginas permitidas',
    menusGranted: 'Menus visíveis',
    usersInGroup: 'Usuários',
    statusActive: 'Ativo',
    statusInactive: 'Desativado',
    groupActive: 'Grupo ativo',
    confirmDeleteGroup: 'Excluir este grupo?',
    cannotDeleteHasUsers: 'Não é possível excluir grupos com usuários.',
    cannotDeleteSystemAdmin: 'O grupo System Admin não pode ser excluído.',
    deleteGroupTitle: 'Excluir grupo',
    deleteEmptyGroupMessage: 'Nenhum usuário pertence a este grupo. Excluí-lo definitivamente?',
    deleteGroupHasUsers: 'Este grupo tem {n} usuário(s). Escolha outro grupo para movê-los antes de excluir.',
    pickTargetGroup: 'Escolher grupo destino',
    targetGroupLabel: 'Grupo destino',
    reassignAndDelete: 'Mover e excluir',
    reassignedAndDeleted: 'Movidos {n} usuários e grupo excluído.',
    usersInThisGroup: 'Usuários neste grupo',
    addMenu: 'Novo link',
    editMenu: 'Editar link',
    textKey: 'Texto exibido',
    path: 'Caminho',
    pageName: 'Chave da página',
    permissionKey: 'Chave da permissão',
    icon: 'Ícone',
    parent: 'Link pai',
    none: 'Nenhum',
    sort: 'Ordem',
    active: 'Ativo',
    menuVisibility: 'Visível para grupos',
    menuVisibilityHint: 'Escolha os grupos que veem este link na barra lateral.',
    pickGroupToEdit: 'Escolha um grupo para editar a visibilidade',
    visible: 'Visível',
    hidden: 'Oculto',
    pickGroup: 'Escolher grupo',
    pickGroupHint: 'O acesso à página é independente da visibilidade na barra lateral.',
    allPages: 'Todas as páginas',
    enabledPages: 'Permitidas',
    selectAll: 'Selecionar todas',
    clearAll: 'Limpar todas',
    pageNotLinked: 'Sub-página',
    pageInMenu: 'Na barra lateral',
    noResults: 'Nenhum resultado para a pesquisa',
  },
};

// ---------------------------------------------------------------------------
// Page category metadata — mirrors the backend registry group_key values
// ---------------------------------------------------------------------------

const CATEGORIES = [
  { key: 'other', ar: 'عام', en: 'General', pt: 'Geral' },
  { key: 'owners', ar: 'الملاك والخيل', en: 'Owners & horses', pt: 'Donos & cavalos' },
  { key: 'veterinary', ar: 'الطب البيطري', en: 'Veterinary', pt: 'Veterinária' },
  { key: 'reproduction', ar: 'الإنتاج', en: 'Reproduction', pt: 'Reprodução' },
  { key: 'farrier', ar: 'البيطار', en: 'Farrier', pt: 'Ferrador' },
  { key: 'training', ar: 'التدريب والبطولات', en: 'Training & shows', pt: 'Treino' },
  { key: 'accounting', ar: 'المحاسبة', en: 'Accounting', pt: 'Contabilidade' },
  { key: 'preferences', ar: 'الإعدادات', en: 'Settings', pt: 'Configurações' },
  { key: 'site_admin', ar: 'إدارة الموقع', en: 'Site admin', pt: 'Admin do site' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const normalizePageName = (value) =>
  String(value || '').trim().replace(/^\/+/, '').toLowerCase();

const compact = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');

const normalizeSearch = (value) =>
  String(value || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

function matchesSearch(values, query) {
  const needle = normalizeSearch(query);
  if (!needle) return true;
  const compactNeedle = compact(needle);
  return values
    .filter((v) => v !== null && v !== undefined)
    .some((v) => {
      const hay = normalizeSearch(v);
      return hay.includes(needle) || (compactNeedle && compact(hay).includes(compactNeedle));
    });
}

function titleCase(value) {
  return String(value || '')
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function fallbackLabel(pageName) {
  return titleCase(
    String(pageName || '')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/[/_-]+/g, ' ')
  );
}

async function fetchJSON(path, options = {}) {
  const token = localStorage.getItem('token') || '';
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch (_) {
    data = text ? { message: text } : null;
  }
  if (!res.ok) {
    throw new Error(data?.details || data?.message || data?.error || `HTTP ${res.status}`);
  }
  return data;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function MetricChip({ label, value, color = COLORS.gold, soft = COLORS.goldSoft, Icon = ShieldIcon }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 2, minHeight: 92 }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: '50%',
            bgcolor: soft,
            color,
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <Icon fontSize="small" />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ color, fontWeight: 900, display: 'block' }}>
            {label}
          </Typography>
          <Typography variant="h5" sx={{ color: COLORS.text, fontWeight: 900 }}>
            {value}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// useAccessControlText — Proxy hook that mirrors `text.someKey` reads onto
// `t('access_control_center.someKey')`. Falls back to the inline COPY object
// when i18next returns the raw key path (i.e. JSON didn't load).
// ---------------------------------------------------------------------------
function useAccessControlText(lang) {
  const { t } = useTranslation();
  return useMemo(() => {
    const fallback = COPY[lang] || COPY.en;
    return new Proxy(
      {},
      {
        get: (_target, prop) => {
          const key = String(prop);
          const translated = t(`access_control_center.${key}`);
          // i18next returns the lookup string when the key is missing.
          if (!translated || translated === `access_control_center.${key}`) {
            return fallback[key];
          }
          return translated;
        },
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, lang]);
}

export default function AccessControlCenter() {
  useViewAudit('access-control');
  const { i18n } = useTranslation();
  const lang = i18n.language === 'ar' ? 'ar' : i18n.language === 'pt' ? 'pt' : 'en';
  const text = useAccessControlText(lang);
  const isArabic = lang === 'ar';

  const [tab, setTab] = useState(0);
  const [overview, setOverview] = useState({
    groups: [],
    menus: [],
    pages: [],
    groupPages: [],
    groupMenus: [],
  });
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedMenuGroup, setSelectedMenuGroup] = useState('');
  const [pageDraft, setPageDraft] = useState({}); // {pageName: bool}
  const [menuDraft, setMenuDraft] = useState({}); // {menuId: bool}
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [groupDialog, setGroupDialog] = useState({ open: false, group: null });
  const [deleteGroupDialog, setDeleteGroupDialog] = useState({
    open: false,
    group: null,
    users: [],
    loading: false,
    targetGroupId: '',
    submitting: false,
  });
  const [menuDialog, setMenuDialog] = useState({ open: false, menu: null });
  const [iconOpen, setIconOpen] = useState(false);

  const isSystemAdminSession = Number(localStorage.getItem('role_id') || 0) === 1;

  // ------------- Data loading -------------
  const loadOverview = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchJSON('/access-control/overview');
      setOverview({
        groups: data?.groups || [],
        menus: data?.menus || [],
        pages: data?.pages || [],
        groupPages: data?.groupPages || [],
        groupMenus: data?.groupMenus || [],
      });
      if (!selectedGroup && data?.groups?.length) {
        const firstEditable = data.groups.find((g) => !g.is_system_admin) || data.groups[0];
        setSelectedGroup(String(firstEditable.group_id));
      }
      if (!selectedMenuGroup && data?.groups?.length) {
        const firstEditable = data.groups.find((g) => !g.is_system_admin) || data.groups[0];
        setSelectedMenuGroup(String(firstEditable.group_id));
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Drafts are cleared whenever the focused group changes.
  useEffect(() => {
    setPageDraft({});
  }, [selectedGroup]);

  useEffect(() => {
    setMenuDraft({});
  }, [selectedMenuGroup]);

  // ------------- Derived data -------------
  const groupsById = useMemo(() => {
    const map = new Map();
    overview.groups.forEach((g) => map.set(Number(g.group_id), g));
    return map;
  }, [overview.groups]);

  const linkedPageNames = useMemo(() => {
    const set = new Set();
    overview.menus.forEach((m) => {
      (m.page_names || []).forEach((p) => set.add(normalizePageName(p)));
    });
    return set;
  }, [overview.menus]);

  const pageAccessMap = useMemo(() => {
    // groupId -> Map(pageName -> bool)
    const byGroup = new Map();
    overview.groupPages.forEach((row) => {
      const gid = Number(row.group_id);
      if (!byGroup.has(gid)) byGroup.set(gid, new Map());
      byGroup.get(gid).set(normalizePageName(row.page_name), Number(row.can_access) === 1);
    });
    return byGroup;
  }, [overview.groupPages]);

  const menuAccessMap = useMemo(() => {
    // groupId -> Map(menuId -> bool)
    const byGroup = new Map();
    overview.groupMenus.forEach((row) => {
      const gid = Number(row.group_id);
      if (!byGroup.has(gid)) byGroup.set(gid, new Map());
      byGroup.get(gid).set(Number(row.menu_id), Number(row.can_view) === 1);
    });
    return byGroup;
  }, [overview.groupMenus]);

  const currentGroup = useMemo(
    () => (selectedGroup ? groupsById.get(Number(selectedGroup)) || null : null),
    [groupsById, selectedGroup]
  );
  const currentMenuGroup = useMemo(
    () => (selectedMenuGroup ? groupsById.get(Number(selectedMenuGroup)) || null : null),
    [groupsById, selectedMenuGroup]
  );

  const currentGroupPageMap = useMemo(() => {
    if (!selectedGroup) return new Map();
    return pageAccessMap.get(Number(selectedGroup)) || new Map();
  }, [pageAccessMap, selectedGroup]);

  const currentMenuGroupMap = useMemo(() => {
    if (!selectedMenuGroup) return new Map();
    return menuAccessMap.get(Number(selectedMenuGroup)) || new Map();
  }, [menuAccessMap, selectedMenuGroup]);

  // Per-group counters (used in tab 1 cards).
  const counters = useMemo(() => {
    const m = new Map();
    overview.groups.forEach((g) => {
      const gid = Number(g.group_id);
      const pages = pageAccessMap.get(gid);
      const menus = menuAccessMap.get(gid);
      m.set(gid, {
        pagesGranted: g.is_system_admin
          ? overview.pages.length
          : pages
            ? [...pages.values()].filter(Boolean).length
            : 0,
        menusGranted: g.is_system_admin
          ? overview.menus.length
          : menus
            ? [...menus.values()].filter(Boolean).length
            : 0,
      });
    });
    return m;
  }, [overview.groups, overview.menus.length, overview.pages.length, pageAccessMap, menuAccessMap]);

  // ------------- Pages tab data -------------
  const visiblePages = useMemo(() => {
    return overview.pages.filter((page) =>
      matchesSearch(
        [
          page.page_name,
          page.route,
          page.label_en,
          page.label_ar,
          page.group_key,
          fallbackLabel(page.page_name),
        ],
        query
      )
    );
  }, [overview.pages, query]);

  const pagesByCategory = useMemo(() => {
    const buckets = new Map();
    CATEGORIES.forEach((c) => buckets.set(c.key, []));
    visiblePages.forEach((p) => {
      const key = p.group_key && buckets.has(p.group_key) ? p.group_key : 'other';
      buckets.get(key).push(p);
    });
    CATEGORIES.forEach((c) => {
      buckets.get(c.key).sort((a, b) =>
        (Number(a.sort_order ?? 9999) - Number(b.sort_order ?? 9999)) ||
        String(a.page_name).localeCompare(String(b.page_name))
      );
    });
    return buckets;
  }, [visiblePages]);

  // ------------- Menu tab data -------------
  const visibleMenus = useMemo(() => {
    return overview.menus.filter((m) =>
      matchesSearch(
        [m.text_key, m.path, m.permission_key, ...(m.page_names || [])],
        query
      )
    );
  }, [overview.menus, query]);

  const menusByParent = useMemo(() => {
    const map = new Map();
    visibleMenus.forEach((m) => {
      const pid = m.parent_id ?? 0;
      if (!map.has(pid)) map.set(pid, []);
      map.get(pid).push(m);
    });
    map.forEach((list) => list.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
    return map;
  }, [visibleMenus]);

  // ------------- Mutations -------------
  const saveGroup = async () => {
    const draft = groupDialog.group || {};
    setError('');
    setMessage('');
    try {
      const body = {
        group_name: draft.group_name,
        group_name_ar: draft.group_name_ar,
        description: draft.description,
        is_active: draft.is_active !== false,
      };
      if (draft.group_id) {
        await fetchJSON(`/access-control/groups/${draft.group_id}`, {
          method: 'PUT',
          body: JSON.stringify(body),
        });
      } else {
        await fetchJSON('/access-control/groups', {
          method: 'POST',
          body: JSON.stringify(body),
        });
      }
      setGroupDialog({ open: false, group: null });
      setMessage(text.saved);
      loadOverview();
    } catch (e) {
      setError(e.message);
    }
  };

  // Open the delete-group dialog. Always fetches the assigned-user list first
  // so the admin can either confirm a straight delete (when zero users) or
  // pick a target group to reassign every user before the delete.
  const openDeleteGroup = async (group) => {
    if (!group?.group_id || group.is_system_admin) return;
    setError('');
    setMessage('');
    setDeleteGroupDialog({
      open: true,
      group,
      users: [],
      loading: true,
      targetGroupId: '',
      submitting: false,
    });
    try {
      const data = await fetchJSON(`/access-control/groups/${group.group_id}/users`);
      setDeleteGroupDialog((state) => ({
        ...state,
        users: data?.users || [],
        loading: false,
      }));
    } catch (e) {
      setDeleteGroupDialog((state) => ({ ...state, loading: false }));
      setError(e.message);
    }
  };

  const closeDeleteGroup = () =>
    setDeleteGroupDialog({
      open: false,
      group: null,
      users: [],
      loading: false,
      targetGroupId: '',
      submitting: false,
    });

  const confirmDeleteGroup = async () => {
    const { group, users, targetGroupId } = deleteGroupDialog;
    if (!group?.group_id) return;
    const needsReassign = users.length > 0;
    if (needsReassign && !targetGroupId) {
      setError(text.pickTargetGroup);
      return;
    }
    setError('');
    setMessage('');
    setDeleteGroupDialog((state) => ({ ...state, submitting: true }));
    try {
      if (needsReassign) {
        const result = await fetchJSON(
          `/access-control/groups/${group.group_id}/reassign-and-delete`,
          {
            method: 'POST',
            body: JSON.stringify({ target_group_id: Number(targetGroupId) }),
          }
        );
        setMessage(
          (text.reassignedAndDeleted || 'Reassigned {n} users and deleted the group.')
            .replace('{n}', String(result?.moved ?? users.length))
        );
      } else {
        await fetchJSON(`/access-control/groups/${group.group_id}`, { method: 'DELETE' });
        setMessage(text.saved);
      }
      if (Number(selectedGroup) === Number(group.group_id)) setSelectedGroup('');
      if (Number(selectedMenuGroup) === Number(group.group_id)) setSelectedMenuGroup('');
      closeDeleteGroup();
      loadOverview();
    } catch (e) {
      setDeleteGroupDialog((state) => ({ ...state, submitting: false }));
      setError(e.message);
    }
  };

  const saveMenu = async () => {
    const draft = menuDialog.menu || {};
    setError('');
    setMessage('');
    try {
      const body = {
        text_key: draft.text_key,
        path: draft.path,
        page_name: draft.page_name,
        permission_key: draft.permission_key,
        icon_key: draft.icon_key || 'default',
        parent_id: draft.parent_id || null,
        sort_order: Number(draft.sort_order || 0),
        is_active: draft.is_active !== false ? 1 : 0,
      };
      if (draft.menu_id) {
        await fetchJSON(`/access-control/menus/${draft.menu_id}`, {
          method: 'PUT',
          body: JSON.stringify(body),
        });
      } else {
        await fetchJSON('/access-control/menus', {
          method: 'POST',
          body: JSON.stringify(body),
        });
      }
      setMenuDialog({ open: false, menu: null });
      setMessage(text.saved);
      loadOverview();
    } catch (e) {
      setError(e.message);
    }
  };

  const deleteMenu = async (menu) => {
    if (!menu?.menu_id) return;
    if (!window.confirm(`${text.delete}: ${menu.text_key || menu.path}?`)) return;
    setError('');
    setMessage('');
    try {
      await fetchJSON(`/access-control/menus/${menu.menu_id}`, { method: 'DELETE' });
      setMessage(text.saved);
      loadOverview();
    } catch (e) {
      setError(e.message);
    }
  };

  const savePagePermissions = async () => {
    if (!selectedGroup || currentGroup?.is_system_admin) return;
    const entries = Object.entries(pageDraft);
    if (!entries.length) return;
    setError('');
    setMessage('');
    try {
      const body = {
        pages: entries.map(([page_name, can_access]) => ({
          page_name,
          can_access: can_access ? 1 : 0,
        })),
      };
      await fetchJSON(`/access-control/groups/${selectedGroup}/pages`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      setPageDraft({});
      setMessage(text.saved);
      loadOverview();
      try { window.dispatchEvent(new Event('smart-menu-refresh')); } catch (_) {}
    } catch (e) {
      setError(e.message);
    }
  };

  const saveMenuPermissions = async () => {
    if (!selectedMenuGroup || currentMenuGroup?.is_system_admin) return;
    const entries = Object.entries(menuDraft);
    if (!entries.length) return;
    setError('');
    setMessage('');
    try {
      const body = {
        menus: entries.map(([menu_id, can_view]) => ({
          menu_id: Number(menu_id),
          can_view: can_view ? 1 : 0,
        })),
      };
      await fetchJSON(`/access-control/groups/${selectedMenuGroup}/menus`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      setMenuDraft({});
      setMessage(text.saved);
      loadOverview();
      try { window.dispatchEvent(new Event('smart-menu-refresh')); } catch (_) {}
    } catch (e) {
      setError(e.message);
    }
  };

  const syncPages = async () => {
    setError('');
    setMessage('');
    try {
      await fetchJSON('/access-control/sync-pages', { method: 'POST', body: JSON.stringify({}) });
      setMessage(text.saved);
      loadOverview();
    } catch (e) {
      setError(e.message);
    }
  };

  // ------------- Page toggle helpers -------------
  const togglePage = (pageName, value) => {
    setPageDraft((prev) => ({ ...prev, [normalizePageName(pageName)]: value }));
  };

  const bulkTogglePages = (pages, value) => {
    setPageDraft((prev) => {
      const next = { ...prev };
      pages.forEach((p) => {
        next[normalizePageName(p.page_name)] = value;
      });
      return next;
    });
  };

  const toggleMenu = (menuId, value) => {
    setMenuDraft((prev) => ({ ...prev, [menuId]: value }));
  };

  // ------------- Render -------------

  return (
    <Box className="smart-page-container" sx={{ minHeight: '100vh', bgcolor: COLORS.page, maxWidth: 'var(--app-page-max)', mx: 'auto' }}>
      {/* Header */}
      <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2, md: 3 }, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
          <Box>
            <Typography variant="h4" sx={{ color: COLORS.text, fontWeight: 900 }}>
              {text.title}
            </Typography>
            <Typography sx={{ color: COLORS.muted, mt: 0.5 }}>{text.subtitle}</Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button startIcon={<RefreshIcon />} onClick={loadOverview} disabled={loading} sx={{ color: COLORS.gold }}>
              {text.refresh}
            </Button>
            <Button startIcon={<SyncIcon />} variant="outlined" onClick={syncPages} sx={{ borderColor: COLORS.gold, color: COLORS.gold }}>
              {text.syncPages}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}
      {!isSystemAdminSession && <Alert severity="warning" sx={{ mb: 2 }}>{text.adminWarning}</Alert>}

      {/* Top metrics */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <MetricChip label={text.tabGroups} value={overview.groups.length} Icon={GroupsIcon} />
        </Grid>
        <Grid item xs={12} md={3}>
          <MetricChip label={text.tabMenus} value={overview.menus.length} color={COLORS.blue} soft={COLORS.blueSoft} Icon={MenuOpenIcon} />
        </Grid>
        <Grid item xs={12} md={3}>
          <MetricChip label={text.tabPages} value={overview.pages.length} color={COLORS.green} soft={COLORS.greenSoft} Icon={PolicyIcon} />
        </Grid>
        <Grid item xs={12} md={3}>
          <MetricChip
            label={text.usersInGroup}
            value={overview.groups.reduce((sum, g) => sum + (g.user_count || 0), 0)}
            color={COLORS.rose}
            soft={COLORS.roseSoft}
            Icon={VerifiedUserIcon}
          />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
          sx={{ p: 2, borderBottom: `1px solid ${COLORS.line}` }}
        >
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable">
            <Tab icon={<GroupsIcon fontSize="small" />} iconPosition="start" label={text.tabGroups} />
            <Tab icon={<MenuOpenIcon fontSize="small" />} iconPosition="start" label={text.tabMenus} />
            <Tab icon={<PolicyIcon fontSize="small" />} iconPosition="start" label={text.tabPages} />
          </Tabs>
          <TextField
            size="small"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={text.search}
            sx={{ width: { xs: '100%', md: 320 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Stack>

        {tab === 0 && (
          <GroupsTab
            text={text}
            isArabic={isArabic}
            groups={overview.groups}
            counters={counters}
            query={query}
            onEdit={(g) => setGroupDialog({ open: true, group: { ...g } })}
            onNew={() =>
              setGroupDialog({
                open: true,
                group: { group_name: '', group_name_ar: '', description: '', is_active: true },
              })
            }
            onDelete={openDeleteGroup}
            onSelect={(gid) => {
              setSelectedGroup(String(gid));
              setSelectedMenuGroup(String(gid));
              setTab(2);
            }}
          />
        )}

        {tab === 1 && (
          <MenusTab
            text={text}
            isArabic={isArabic}
            groups={overview.groups}
            visibleMenus={visibleMenus}
            menusByParent={menusByParent}
            selectedMenuGroup={selectedMenuGroup}
            setSelectedMenuGroup={setSelectedMenuGroup}
            currentMenuGroup={currentMenuGroup}
            currentMenuGroupMap={currentMenuGroupMap}
            menuDraft={menuDraft}
            toggleMenu={toggleMenu}
            onNewMenu={() =>
              setMenuDialog({
                open: true,
                menu: {
                  text_key: '',
                  path: '',
                  page_name: '',
                  permission_key: '',
                  icon_key: 'default',
                  parent_id: null,
                  sort_order: (overview.menus.length + 1) * 10,
                  is_active: true,
                },
              })
            }
            onEditMenu={(m) => setMenuDialog({ open: true, menu: { ...m } })}
            onDeleteMenu={deleteMenu}
            onSave={saveMenuPermissions}
            saveDisabled={
              !selectedMenuGroup ||
              currentMenuGroup?.is_system_admin ||
              !Object.keys(menuDraft).length
            }
          />
        )}

        {tab === 2 && (
          <PagesTab
            text={text}
            isArabic={isArabic}
            groups={overview.groups}
            pagesByCategory={pagesByCategory}
            visiblePages={visiblePages}
            selectedGroup={selectedGroup}
            setSelectedGroup={setSelectedGroup}
            currentGroup={currentGroup}
            currentGroupPageMap={currentGroupPageMap}
            linkedPageNames={linkedPageNames}
            pageDraft={pageDraft}
            togglePage={togglePage}
            bulkTogglePages={bulkTogglePages}
            onSave={savePagePermissions}
            saveDisabled={
              !selectedGroup ||
              currentGroup?.is_system_admin ||
              !Object.keys(pageDraft).length
            }
          />
        )}
      </Paper>

      <GroupDialog
        text={text}
        open={groupDialog.open}
        group={groupDialog.group}
        onChange={(updates) =>
          setGroupDialog((state) => ({ ...state, group: { ...state.group, ...updates } }))
        }
        onClose={() => setGroupDialog({ open: false, group: null })}
        onSave={saveGroup}
      />

      <DeleteGroupDialog
        text={text}
        isArabic={isArabic}
        state={deleteGroupDialog}
        groups={overview.groups}
        onTargetChange={(targetGroupId) =>
          setDeleteGroupDialog((state) => ({ ...state, targetGroupId }))
        }
        onClose={closeDeleteGroup}
        onConfirm={confirmDeleteGroup}
      />

      <MenuDialog
        text={text}
        open={menuDialog.open}
        menu={menuDialog.menu}
        menus={overview.menus}
        onChange={(updates) =>
          setMenuDialog((state) => ({ ...state, menu: { ...state.menu, ...updates } }))
        }
        onClose={() => setMenuDialog({ open: false, menu: null })}
        onSave={saveMenu}
        onOpenIcon={() => setIconOpen(true)}
      />

      <IconPicker
        open={iconOpen}
        value={menuDialog.menu?.icon_key}
        onClose={() => setIconOpen(false)}
        onSelect={(key) =>
          setMenuDialog((state) => ({ ...state, menu: { ...state.menu, icon_key: key } }))
        }
      />
    </Box>
  );
}

// ===========================================================================
// TAB 1 — Groups
// ===========================================================================

function GroupsTab({ text, isArabic, groups, counters, query, onEdit, onNew, onDelete, onSelect }) {
  const filtered = useMemo(() => {
    return groups.filter((g) =>
      matchesSearch([g.group_name, g.group_name_ar, g.description], query)
    );
  }, [groups, query]);

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>
          {text.tabGroups}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onNew}
          sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover } }}
        >
          {text.addGroup}
        </Button>
      </Stack>

      <Grid container spacing={2}>
        {filtered.map((group) => {
          const stats = counters.get(Number(group.group_id)) || { pagesGranted: 0, menusGranted: 0 };
          return (
            <Grid item xs={12} sm={6} md={4} key={group.group_id}>
              <Paper
                elevation={0}
                sx={{
                  ...cardSx,
                  p: 2,
                  height: '100%',
                  borderColor: group.is_system_admin ? COLORS.gold : COLORS.line,
                  bgcolor: group.is_system_admin ? COLORS.goldSoft : '#fff',
                  cursor: group.is_system_admin ? 'default' : 'pointer',
                  transition: 'transform 0.15s, box-shadow 0.15s',
                  '&:hover': group.is_system_admin
                    ? {}
                    : { transform: 'translateY(-2px)', boxShadow: 2 },
                }}
                onClick={() => !group.is_system_admin && onSelect(group.group_id)}
              >
                <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>
                        {isArabic ? group.group_name_ar || group.group_name : group.group_name}
                      </Typography>
                      {group.is_system_admin && (
                        <Chip
                          size="small"
                          icon={<LockIcon fontSize="small" />}
                          label={text.systemAdmin}
                          sx={{ bgcolor: COLORS.gold, color: '#fff', fontWeight: 900 }}
                        />
                      )}
                    </Stack>
                    <Typography variant="caption" sx={{ color: COLORS.muted, display: 'block', mt: 0.5 }}>
                      {group.description ||
                        (group.is_system_admin
                          ? text.systemAdminHint
                          : isArabic
                          ? 'لا يوجد وصف'
                          : 'No description')}
                    </Typography>
                  </Box>
                  {!group.is_system_admin && (
                    <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                      <Tooltip title={text.edit}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEdit(group);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={text.delete}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete(group);
                          }}
                          sx={{ color: COLORS.rose }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )}
                </Stack>

                <Divider sx={{ my: 1.5 }} />

                <Stack direction="row" spacing={1.5}>
                  <Stack alignItems="center" sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: COLORS.muted }}>
                      {text.pagesGranted}
                    </Typography>
                    <Chip
                      size="small"
                      label={stats.pagesGranted}
                      sx={{ bgcolor: COLORS.greenSoft, color: COLORS.text, fontWeight: 900, mt: 0.5 }}
                    />
                  </Stack>
                  <Stack alignItems="center" sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: COLORS.muted }}>
                      {text.menusGranted}
                    </Typography>
                    <Chip
                      size="small"
                      label={stats.menusGranted}
                      sx={{ bgcolor: COLORS.blueSoft, color: COLORS.text, fontWeight: 900, mt: 0.5 }}
                    />
                  </Stack>
                  <Stack alignItems="center" sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: COLORS.muted }}>
                      {text.usersInGroup}
                    </Typography>
                    <Chip
                      size="small"
                      label={group.user_count || 0}
                      sx={{ bgcolor: COLORS.roseSoft, color: COLORS.text, fontWeight: 900, mt: 0.5 }}
                    />
                  </Stack>
                </Stack>
              </Paper>
            </Grid>
          );
        })}
        {!filtered.length && (
          <Grid item xs={12}>
            <Alert severity="info">{text.noResults}</Alert>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

// ===========================================================================
// TAB 2 — Sidebar Menu (CRUD + per-group visibility)
// ===========================================================================

function MenusTab({
  text,
  isArabic,
  groups,
  visibleMenus,
  menusByParent,
  selectedMenuGroup,
  setSelectedMenuGroup,
  currentMenuGroup,
  currentMenuGroupMap,
  menuDraft,
  toggleMenu,
  onNewMenu,
  onEditMenu,
  onDeleteMenu,
  onSave,
  saveDisabled,
}) {
  const renderRow = (menu, level = 0) => {
    const Icon = getSidebarIcon(menu);
    const draftVal = menuDraft[menu.menu_id];
    const stored = currentMenuGroupMap.get(menu.menu_id) ?? false;
    const isVisible = draftVal === undefined ? stored : draftVal;
    const isSystemAdminGroup = currentMenuGroup?.is_system_admin;

    return (
      <React.Fragment key={menu.menu_id}>
        <TableRow hover>
          <TableCell sx={{ width: 64 }}>
            <Box sx={{ pl: `${level * 16}px`, color: COLORS.gold, display: 'flex', '& svg': { width: 22, height: 22 } }}>
              <Icon />
            </Box>
          </TableCell>
          <TableCell sx={{ fontWeight: 900, color: COLORS.text }}>{menu.text_key}</TableCell>
          <TableCell sx={{ color: COLORS.muted }}>{menu.path}</TableCell>
          <TableCell>
            <Chip
              size="small"
              label={Number(menu.is_active) === 1 ? text.statusActive : text.statusInactive}
              sx={{
                bgcolor: Number(menu.is_active) === 1 ? COLORS.greenSoft : COLORS.roseSoft,
                color: COLORS.text,
                fontWeight: 800,
              }}
            />
          </TableCell>
          <TableCell align="center">
            {currentMenuGroup ? (
              <Switch
                checked={isSystemAdminGroup ? true : Boolean(isVisible)}
                disabled={isSystemAdminGroup}
                onChange={(e) => toggleMenu(menu.menu_id, e.target.checked)}
                color="success"
              />
            ) : (
              <Typography variant="caption" sx={{ color: COLORS.muted }}>—</Typography>
            )}
          </TableCell>
          <TableCell align="right">
            <Tooltip title={text.edit}>
              <IconButton size="small" onClick={() => onEditMenu(menu)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={text.delete}>
              <IconButton size="small" onClick={() => onDeleteMenu(menu)} sx={{ color: COLORS.rose }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </TableCell>
        </TableRow>
        {(menusByParent.get(menu.menu_id) || []).map((child) => renderRow(child, level + 1))}
      </React.Fragment>
    );
  };

  const rootMenus = menusByParent.get(0) || menusByParent.get(null) || [];

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>
            {text.tabMenus}
          </Typography>
          <Typography variant="body2" sx={{ color: COLORS.muted }}>
            {text.menuVisibilityHint}
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <Select
              value={selectedMenuGroup || ''}
              displayEmpty
              onChange={(e) => setSelectedMenuGroup(e.target.value)}
            >
              <MenuItem value="">{text.pickGroupToEdit}</MenuItem>
              {groups.map((g) => (
                <MenuItem key={g.group_id} value={String(g.group_id)}>
                  {isArabic ? g.group_name_ar || g.group_name : g.group_name}
                  {g.is_system_admin ? ` (${text.systemAdmin})` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" startIcon={<AddIcon />} onClick={onNewMenu} sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover } }}>
            {text.addMenu}
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={onSave}
            disabled={saveDisabled}
            sx={{ bgcolor: COLORS.green, '&:hover': { bgcolor: COLORS.green } }}
          >
            {text.save}
          </Button>
        </Stack>
      </Stack>

      {currentMenuGroup?.is_system_admin && (
        <Alert severity="info" sx={{ mb: 2 }}>{text.systemAdminHint}</Alert>
      )}

      <TableContainer sx={smartTableContainerSx}>
        <Table size="small" sx={smartTableSx}>
          <TableHead sx={smartTableHeadSx}>
            <TableRow>
              <TableCell>{text.icon}</TableCell>
              <TableCell>{text.textKey}</TableCell>
              <TableCell>{text.path}</TableCell>
              <TableCell>{text.active}</TableCell>
              <TableCell align="center">
                {currentMenuGroup
                  ? isArabic
                    ? `${text.visible}؟`
                    : text.visible
                  : text.menuVisibility}
              </TableCell>
              <TableCell align="right">{text.edit}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rootMenus.length ? (
              rootMenus.map((m) => renderRow(m))
            ) : (
              <TableRow>
                <TableCell colSpan={6}>
                  <Alert severity="info">{text.noResults}</Alert>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ===========================================================================
// TAB 3 — Page Access
// ===========================================================================

function PagesTab({
  text,
  isArabic,
  groups,
  pagesByCategory,
  visiblePages,
  selectedGroup,
  setSelectedGroup,
  currentGroup,
  currentGroupPageMap,
  linkedPageNames,
  pageDraft,
  togglePage,
  bulkTogglePages,
  onSave,
  saveDisabled,
}) {
  const pickLabel = (page) => {
    const label = isArabic ? page.label_ar : page.label_en;
    return label || fallbackLabel(page.page_name);
  };

  const isPageAllowed = (page) => {
    const key = normalizePageName(page.page_name);
    const draft = pageDraft[key];
    if (draft !== undefined) return draft;
    if (currentGroup?.is_system_admin) return true;
    return currentGroupPageMap.get(key) === true;
  };

  const allowedCount = useMemo(() => {
    if (!currentGroup) return 0;
    if (currentGroup.is_system_admin) return visiblePages.length;
    return visiblePages.filter(isPageAllowed).length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visiblePages, currentGroup, pageDraft, currentGroupPageMap]);

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>
            {text.tabPages}
          </Typography>
          <Typography variant="body2" sx={{ color: COLORS.muted }}>
            {text.pickGroupHint}
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <Select value={selectedGroup || ''} displayEmpty onChange={(e) => setSelectedGroup(e.target.value)}>
              <MenuItem value="">{text.pickGroup}</MenuItem>
              {groups.map((g) => (
                <MenuItem key={g.group_id} value={String(g.group_id)}>
                  {isArabic ? g.group_name_ar || g.group_name : g.group_name}
                  {g.is_system_admin ? ` (${text.systemAdmin})` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Chip
            label={`${text.enabledPages}: ${allowedCount}/${visiblePages.length}`}
            sx={{ bgcolor: COLORS.greenSoft, color: COLORS.text, fontWeight: 900 }}
          />
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={onSave}
            disabled={saveDisabled}
            sx={{ bgcolor: COLORS.green, '&:hover': { bgcolor: COLORS.green } }}
          >
            {text.save}
          </Button>
        </Stack>
      </Stack>

      {!selectedGroup && <Alert severity="info">{text.pickGroup}</Alert>}
      {currentGroup?.is_system_admin && (
        <Alert severity="info" sx={{ mb: 2 }}>{text.systemAdminHint}</Alert>
      )}

      {selectedGroup && (
        <Stack spacing={2}>
          {CATEGORIES.map((category) => {
            const pages = pagesByCategory.get(category.key) || [];
            if (!pages.length) return null;
            const allOn = pages.every(isPageAllowed);
            const someOn = pages.some(isPageAllowed);
            const isSystemAdmin = currentGroup?.is_system_admin;
            return (
              <Paper key={category.key} elevation={0} sx={{ ...cardSx, p: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Checkbox
                      checked={allOn}
                      indeterminate={!allOn && someOn}
                      disabled={isSystemAdmin}
                      onChange={(e) => bulkTogglePages(pages, e.target.checked)}
                      sx={{ color: COLORS.gold, '&.Mui-checked': { color: COLORS.green } }}
                    />
                    <Box>
                      <Typography sx={{ color: COLORS.text, fontWeight: 900 }}>
                        {category[isArabic ? 'ar' : 'en']}
                      </Typography>
                      <Typography variant="caption" sx={{ color: COLORS.muted }}>
                        {pages.length} {isArabic ? 'صفحة' : 'pages'} ·{' '}
                        {pages.filter(isPageAllowed).length} {text.enabledPages.toLowerCase()}
                      </Typography>
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={1}>
                    <Button size="small" disabled={isSystemAdmin} onClick={() => bulkTogglePages(pages, true)} sx={{ color: COLORS.green }}>
                      {text.selectAll}
                    </Button>
                    <Button size="small" disabled={isSystemAdmin} onClick={() => bulkTogglePages(pages, false)} sx={{ color: COLORS.rose }}>
                      {text.clearAll}
                    </Button>
                  </Stack>
                </Stack>

                <Grid container spacing={1}>
                  {pages.map((page) => {
                    const allowed = isPageAllowed(page);
                    const linked = linkedPageNames.has(normalizePageName(page.page_name));
                    return (
                      <Grid item xs={12} sm={6} md={4} key={page.page_name}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 1.25,
                            border: `1px solid ${allowed ? COLORS.green : COLORS.line}`,
                            borderRadius: 1.5,
                            bgcolor: allowed ? COLORS.greenSoft : '#fff',
                          }}
                        >
                          <Stack direction="row" spacing={1} alignItems="flex-start">
                            <Checkbox
                              size="small"
                              checked={Boolean(allowed)}
                              disabled={isSystemAdmin}
                              onChange={(e) => togglePage(page.page_name, e.target.checked)}
                              sx={{ p: 0.25, color: COLORS.gold, '&.Mui-checked': { color: COLORS.green } }}
                            />
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="space-between">
                                <Typography sx={{ color: COLORS.text, fontWeight: 800, fontSize: 14 }}>
                                  {pickLabel(page)}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={linked ? text.pageInMenu : text.pageNotLinked}
                                  sx={{
                                    bgcolor: linked ? COLORS.blueSoft : COLORS.goldSoft,
                                    color: COLORS.text,
                                    fontWeight: 700,
                                    fontSize: 10,
                                  }}
                                />
                              </Stack>
                              <Typography variant="caption" sx={{ color: COLORS.muted, display: 'block' }}>
                                {page.route || page.page_name}
                              </Typography>
                            </Box>
                          </Stack>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              </Paper>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}

// ===========================================================================
// Dialogs
// ===========================================================================

function GroupDialog({ text, open, group, onChange, onClose, onSave }) {
  const isEdit = Boolean(group?.group_id);
  const isSystemAdmin = group?.is_system_admin;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit ? text.editGroup : text.addGroup}
        {isSystemAdmin && (
          <Chip
            size="small"
            icon={<LockIcon fontSize="small" />}
            label={text.systemAdmin}
            sx={{ ml: 1, bgcolor: COLORS.gold, color: '#fff', fontWeight: 900 }}
          />
        )}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {isSystemAdmin && <Alert severity="warning">{text.systemAdminHint}</Alert>}
          <TextField
            label={text.groupName}
            value={group?.group_name || ''}
            onChange={(e) => onChange({ group_name: e.target.value })}
            disabled={isSystemAdmin}
            fullWidth
          />
          <TextField
            label={text.groupNameAr}
            value={group?.group_name_ar || ''}
            onChange={(e) => onChange({ group_name_ar: e.target.value })}
            disabled={isSystemAdmin}
            fullWidth
          />
          <TextField
            label={text.groupDescription}
            value={group?.description || ''}
            onChange={(e) => onChange({ description: e.target.value })}
            disabled={isSystemAdmin}
            multiline
            minRows={2}
            fullWidth
          />
          {!isSystemAdmin && (
            <FormControlLabel
              control={
                <Switch
                  checked={group?.is_active !== false}
                  onChange={(e) => onChange({ is_active: e.target.checked })}
                  color="success"
                />
              }
              label={text.groupActive}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{text.cancel}</Button>
        <Button
          variant="contained"
          onClick={onSave}
          disabled={isSystemAdmin}
          sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover } }}
        >
          {text.save}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function MenuDialog({ text, open, menu, menus, onChange, onClose, onSave, onOpenIcon }) {
  const parents = useMemo(() => {
    return (menus || [])
      .filter((m) => Number(m.is_active ?? 1) === 1 && (!menu?.menu_id || m.menu_id !== menu.menu_id))
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  }, [menus, menu?.menu_id]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{menu?.menu_id ? text.editMenu : text.addMenu}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ pt: 1 }}>
          <Grid item xs={12} md={6}>
            <TextField
              label={text.textKey}
              fullWidth
              value={menu?.text_key || ''}
              onChange={(e) => onChange({ text_key: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label={text.path}
              fullWidth
              value={menu?.path || ''}
              onChange={(e) => onChange({ path: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label={text.pageName}
              fullWidth
              value={menu?.page_name || ''}
              onChange={(e) => onChange({ page_name: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label={text.permissionKey}
              fullWidth
              value={menu?.permission_key || ''}
              onChange={(e) => onChange({ permission_key: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label={text.icon}
              fullWidth
              value={menu?.icon_key || 'default'}
              onClick={onOpenIcon}
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <Select
                value={menu?.parent_id || ''}
                displayEmpty
                onChange={(e) => onChange({ parent_id: e.target.value ? Number(e.target.value) : null })}
              >
                <MenuItem value="">{text.none}</MenuItem>
                {parents.map((p) => (
                  <MenuItem key={p.menu_id} value={p.menu_id}>
                    {p.text_key} ({p.path})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label={text.sort}
              type="number"
              fullWidth
              value={menu?.sort_order ?? 0}
              onChange={(e) => onChange({ sort_order: Number(e.target.value) })}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={menu?.is_active !== false && Number(menu?.is_active ?? 1) === 1}
                  onChange={(e) => onChange({ is_active: e.target.checked })}
                  color="success"
                />
              }
              label={text.active}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{text.cancel}</Button>
        <Button
          variant="contained"
          onClick={onSave}
          sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover } }}
        >
          {text.save}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// DeleteGroupDialog — confirms deletion. When the group still has users, it
// blocks a straight DELETE (which would 409) and asks the admin to pick a
// target group to reassign every user in one atomic call.
// ---------------------------------------------------------------------------

function DeleteGroupDialog({
  text,
  isArabic,
  state,
  groups,
  onTargetChange,
  onClose,
  onConfirm,
}) {
  const { open, group, users, loading, targetGroupId, submitting } = state;
  if (!group) return null;
  const hasUsers = users.length > 0;
  const eligibleTargets = (groups || []).filter(
    (g) => Number(g.group_id) !== Number(group.group_id) && g.is_active !== false
  );

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: COLORS.text, fontWeight: 900 }}>
        {text.deleteGroupTitle}
        <Chip
          size="small"
          label={isArabic ? group.group_name_ar || group.group_name : group.group_name}
          sx={{ ml: 1, bgcolor: COLORS.goldSoft, color: COLORS.text, fontWeight: 900 }}
        />
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Stack alignItems="center" sx={{ py: 3 }}>
            <Typography variant="body2" sx={{ color: COLORS.muted }}>
              {isArabic ? 'جارٍ التحميل...' : 'Loading...'}
            </Typography>
          </Stack>
        ) : !hasUsers ? (
          <Alert severity="warning" sx={{ mb: 0 }}>
            {text.deleteEmptyGroupMessage}
          </Alert>
        ) : (
          <Stack spacing={2}>
            <Alert severity="warning">
              {text.deleteGroupHasUsers.replace('{n}', String(users.length))}
            </Alert>
            <Box>
              <Typography
                variant="caption"
                sx={{ color: COLORS.gold, fontWeight: 900, display: 'block', mb: 0.5 }}
              >
                {text.usersInThisGroup} ({users.length})
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  maxHeight: 160,
                  overflowY: 'auto',
                  border: `1px solid ${COLORS.line}`,
                  bgcolor: COLORS.cardSoft,
                  p: 0.5,
                }}
              >
                <Stack spacing={0.25}>
                  {users.map((u) => (
                    <Box
                      key={u.user_id}
                      sx={{
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        bgcolor: '#fff',
                        fontSize: 13,
                      }}
                    >
                      <Typography
                        component="span"
                        sx={{ color: COLORS.text, fontWeight: 800, mr: 1 }}
                      >
                        {u.username}
                      </Typography>
                      <Typography component="span" sx={{ color: COLORS.muted, fontSize: 12 }}>
                        {[u.first_name, u.last_name].filter(Boolean).join(' ') || '—'}
                        {u.email ? ` · ${u.email}` : ''}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            </Box>
            <FormControl fullWidth>
              <InputLabelProxy label={text.targetGroupLabel} />
              <Select
                value={targetGroupId || ''}
                onChange={(e) => onTargetChange(e.target.value)}
                displayEmpty
              >
                <MenuItem value="">{text.pickTargetGroup}</MenuItem>
                {eligibleTargets.map((g) => (
                  <MenuItem key={g.group_id} value={g.group_id}>
                    {isArabic ? g.group_name_ar || g.group_name : g.group_name}
                    {g.is_system_admin ? ` (${text.systemAdmin})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {text.cancel}
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={loading || submitting || (hasUsers && !targetGroupId)}
          sx={{
            bgcolor: hasUsers ? COLORS.gold : COLORS.rose,
            color: '#fff',
            '&:hover': { bgcolor: hasUsers ? COLORS.goldHover : COLORS.rose },
          }}
        >
          {hasUsers ? text.reassignAndDelete : text.delete}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Tiny shim: the Select above sits inside a FormControl without a separate
// InputLabel because the empty MenuItem already carries the "pick a target"
// hint. This component renders an accessible label without the floating
// MUI animation that would overlap with `displayEmpty` text.
function InputLabelProxy({ label }) {
  return (
    <Typography
      variant="caption"
      sx={{ color: COLORS.muted, fontWeight: 800, mb: 0.5, display: 'block' }}
    >
      {label}
    </Typography>
  );
}
